import React, { useEffect, useMemo, useState } from "react";
import { ScrollView, View, Text, Pressable, StyleSheet, TouchableOpacity } from "react-native";
import { Ionicons, Octicons } from "@expo/vector-icons";
import { Provider } from "react-native-paper";
import { deleteField } from "firebase/firestore";
import AdminInput from "../../../components/AdminInput";
import AdminSwitch from "../../../components/AdminSwitch";
import { AlertBox } from "../../../components/AlertBox";
import FlatlistMultipleSelection from "../../ClickCard/FlatListMultipleSelection";
import { ADMIN_COLORS, ADMIN_FONT } from "../../../config/theme/adminColors";
import {
  SectionCard,
  Field,
  FieldLabel,
  InfoBanner,
} from "../../AdminPanel/Products/formStyles";
import { SmallText } from "../../../config/theme/globalStyles";
import EntityFilterLinksSection from "../../../components/EntityFilterLinksSection";
import { useGlobalFilters } from "../../../hooks/useGlobalFilters";
import { useProductCatalogFiltersForCard } from "../../../hooks/useProductCatalogFiltersForCard";
import { useRemoteConfigContext } from "../../../contexts/useRemoteConfigContext";
import {
  buildFiltersBySlug,
  getDirectCardFilterSlugs,
  syncEntityFilterLinks,
} from "../../../utils/syncEntityFilterLinks";
import { syncProductFilterLinks } from "../../../utils/syncProductFilterLinks";

const EditCardInformationModal = (props) => {
  const {
    card,
    categoryId,
    cardId: cardIdProp,
    categoryFilterIds = [],
    plans,
    courses,
    products,
    isSaving,
    onClose,
    onSave,
  } = props;
  const { title, archived, order, description, hideTitle } = card || {};
  const { hasProductCatalog } = useRemoteConfigContext();
  const resolvedCardId = card?.id || cardIdProp;

  const [showSaveSuccessAlert, setShowSaveSuccessAlert] = useState(false);

  const [updatedCard, setUpdatedCard] = useState({
    title,
    archived,
    order,
    description,
    // Sempre booleano: o objeto inteiro vai para o `updateDoc`, e o Firestore
    // recusa `undefined` em qualquer campo.
    hideTitle: !!hideTitle,
  });

  const [updatedCourses, setUpdatedCourses] = useState([...courses]);
  const [updatedPlans, setUpdatedPlans] = useState([...plans]);
  const [updatedProducts, setUpdatedProducts] = useState([...products]);

  const { filters, prefilterByTagFirst, loading: filtersLoading } = useGlobalFilters();
  const filtersBySlug = useMemo(() => buildFiltersBySlug(filters), [filters]);
  const initialDirectFilterSlugs = useMemo(
    () => getDirectCardFilterSlugs(card?.filterIds, categoryFilterIds),
    [card?.filterIds, categoryFilterIds]
  );
  const [selectedFilterSlugs, setSelectedFilterSlugs] = useState(initialDirectFilterSlugs);

  const selectedAccessProducts = useMemo(
    () => updatedProducts.filter((product) => product?.selected),
    [updatedProducts]
  );
  const selectedAccessPlans = useMemo(
    () => updatedPlans.filter((plan) => plan?.selected),
    [updatedPlans]
  );
  const selectedAccessCourses = useMemo(
    () => updatedCourses.filter((course) => course?.selected),
    [updatedCourses]
  );

  const {
    groups: productFilterGroupsRaw,
    initialSelectedKeys: initialProductFilterKeys,
    loading: productFiltersLoading,
  } = useProductCatalogFiltersForCard({
    enabled: hasProductCatalog,
    categoryId,
    cardId: resolvedCardId,
    selectedProducts: selectedAccessProducts,
    selectedPlans: selectedAccessPlans,
    selectedCourses: selectedAccessCourses,
  });

  const productFilterGroups = useMemo(
    () =>
      productFilterGroupsRaw.map((group) => ({
        key: group.key,
        label: group.label,
        filters: group.filters,
      })),
    [productFilterGroupsRaw]
  );

  const [selectedProductFilterKeys, setSelectedProductFilterKeys] = useState(
    initialProductFilterKeys
  );

  useEffect(() => {
    setSelectedFilterSlugs(initialDirectFilterSlugs);
  }, [card?.id, initialDirectFilterSlugs]);

  useEffect(() => {
    setSelectedProductFilterKeys(initialProductFilterKeys);
  }, [card?.id, initialProductFilterKeys.join("|")]);

  const toggleFilterSlug = (slug) => {
    setSelectedFilterSlugs((prev) =>
      prev.includes(slug) ? prev.filter((item) => item !== slug) : [...prev, slug]
    );
  };

  const toggleProductFilterKey = (filterKey) => {
    setSelectedProductFilterKeys((prev) =>
      prev.includes(filterKey) ? prev.filter((item) => item !== filterKey) : [...prev, filterKey]
    );
  };

  const handleOnSaveClick = async () => {
    const plansCard = updatedPlans?.filter((plan) => plan?.selected);
    const coursesCard = updatedCourses?.filter((course) => course?.selected);
    const productsCard = updatedProducts?.filter((product) => product?.selected);

    // Criar um array de strings contendo apenas os productIds dos produtos selecionados
    const selectedProductIds = productsCard?.map((product) => product?.productId);

    //it needs to be { plans: plans }, even if the name of the variable is equal, because if its only plans, it saves on wrong format on firestore
    await onSave({
      ...updatedCard,
      ...(plansCard?.length > 0 && { plans: plansCard }),
      ...(plansCard?.length <= 0 && { plans: deleteField() }),
      ...(coursesCard?.length > 0 && { coursesCard: coursesCard }),
      ...(coursesCard?.length <= 0 && { coursesCard: deleteField() }),
      ...(selectedProductIds?.length > 0 && { productIds: selectedProductIds }),
      ...(selectedProductIds?.length <= 0 && { productIds: deleteField() }),
    });

    const resolvedCardIdOnSave = card?.id || cardIdProp;
    if (categoryId && resolvedCardIdOnSave) {
      await syncEntityFilterLinks({
        entityType: "card",
        categoryId,
        cardId: resolvedCardIdOnSave,
        previousSlugs: initialDirectFilterSlugs,
        nextSlugs: selectedFilterSlugs,
        filtersBySlug,
      });

      if (hasProductCatalog) {
        await syncProductFilterLinks({
          categoryId,
          cardId: resolvedCardIdOnSave,
          previousKeys: initialProductFilterKeys,
          nextKeys: selectedProductFilterKeys,
        });
      }
    }

    setShowSaveSuccessAlert(true);
  };

  const isArchived = updatedCard.archived;

  // Módulo restrito = tem ao menos um plano/curso/produto MARCADO. A lista de
  // opções disponíveis não conta: o que restringe é a seleção, e é ela que vira
  // `plans`/`coursesCard`/`productIds` no save.
  const hasAccessRule =
    updatedPlans?.some((plan) => plan?.selected) ||
    updatedCourses?.some((course) => course?.selected) ||
    updatedProducts?.some((product) => product?.selected);

  return (
    <Provider>
      <ScrollView
        style={{ backgroundColor: ADMIN_COLORS.surfaceSubtle }}
        contentContainerStyle={styles.scrollContent}
      >
        <View style={styles.page}>
          <TouchableOpacity
            onPress={onClose}
            style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginBottom: 12, alignSelf: 'flex-start' }}
            >
            <Octicons name="chevron-left" size={18} color={ADMIN_COLORS.muted} />
            <SmallText color={ADMIN_COLORS.muted}>
              Voltar
            </SmallText>
          </TouchableOpacity>
          <Text style={styles.pageTitle}>Alterar Informações</Text>
          <Text style={styles.pageSubtitle}>
            Altere a ordem em que esse módulo aparece na tela principal, o título, se ele fica
            visível para os alunos e a descrição.
          </Text>

          <SectionCard title="Dados do módulo">
            <Field>
              <FieldLabel>Ordem</FieldLabel>
              <AdminInput
                keyboardType="numeric"
                placeholder="Digitar ordem"
                value={updatedCard.order}
                onChangeText={(value) =>
                  setUpdatedCard((prev) => ({ ...prev, order: Number(value) }))
                }
              />
            </Field>

            <Field>
              <FieldLabel>Título</FieldLabel>
              <AdminInput
                placeholder="Digitar título"
                value={updatedCard.title}
                onChangeText={(value) =>
                  setUpdatedCard((prev) => ({ ...prev, title: value }))
                }
              />
            </Field>

            {/* Mesma configuração que existe na criação do módulo (e agora na
                da categoria): o título continua valendo para busca, filtros e
                acesso mesmo quando não é exibido ao aluno. */}
            <Field>
              <View style={styles.switchRow}>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <FieldLabel>Exibir título para os alunos</FieldLabel>
                  <Text style={styles.switchHint}>
                    Desative para um visual mais limpo — o título continua valendo internamente.
                  </Text>
                </View>
                <AdminSwitch
                  value={!updatedCard.hideTitle}
                  onValueChange={(value) =>
                    setUpdatedCard((prev) => ({ ...prev, hideTitle: !value }))
                  }
                />
              </View>

              {updatedCard.hideTitle ? (
                <View style={styles.warnBox}>
                  <Text style={styles.warnText}>
                    O título ficará invisível para os alunos, mas continua sendo usado na
                    busca, nos filtros e na configuração de acesso. Sem um título claro, fica
                    difícil identificar o módulo nessas telas.
                  </Text>
                </View>
              ) : null}
            </Field>

            <Field>
              <FieldLabel>Descrição</FieldLabel>
              <AdminInput
                multiline
                placeholder="Digitar descrição"
                value={updatedCard.description}
                onChangeText={(value) =>
                  setUpdatedCard((prev) => ({ ...prev, description: value }))
                }
              />
            </Field>
          </SectionCard>

          {/* A seção aparece SEMPRE, mesmo sem nenhum plano/produto cadastrado:
              é aqui que o admin descobre se o módulo está restrito ou aberto. */}
          <SectionCard title="Acesso ao módulo">
            {/* Sem nada vinculado, o card não grava `plans`/`coursesCard`/
                `productIds` — e as telas do aluno tratam essa ausência como
                acesso liberado. O aviso deixa isso explícito em vez de o admin
                ter que deduzir de um campo vazio. */}
            {!hasAccessRule && (
              <View style={{ marginBottom: 14 }}>
                <InfoBanner variant="info">
                  Nenhum plano ou produto vinculado: este módulo está{' '}
                  <Text style={{ fontWeight: '700' }}>gratuito</Text> e aparece para
                  todos os alunos, inclusive quem não comprou.
                </InfoBanner>
              </View>
            )}
            {plans?.length > 0 && (
                <Field>
                  <FieldLabel>Planos Stripe que liberam este card</FieldLabel>
                  <FlatlistMultipleSelection
                    items={updatedPlans}
                    setItems={setUpdatedPlans}
                    smallText="Planos de Assinatura Selecionados:"
                    footerText="Selecione os planos de assinatura"
                  />
                </Field>
              )}
              {courses?.length > 0 && (
                <Field>
                  <FieldLabel>Produtos Stripe que liberam este card</FieldLabel>
                  <FlatlistMultipleSelection
                    items={updatedCourses}
                    setItems={setUpdatedCourses}
                    smallText="Produtos Selecionados:"
                    footerText="Selecione os produtos"
                  />
                </Field>
              )}
              {products?.length > 0 && (
                <Field>
                  <FieldLabel>Produtos criados que liberam este card</FieldLabel>
                  <FlatlistMultipleSelection
                    items={updatedProducts}
                    setItems={setUpdatedProducts}
                    smallText="Produtos Selecionados:"
                    footerText="Selecione os produtos"
                  />
                </Field>
              )}
          </SectionCard>

          <SectionCard title="Filtros">
            <EntityFilterLinksSection
              entityLabel="módulo"
              filters={filters}
              loading={filtersLoading}
              prefilterByTagFirst={prefilterByTagFirst}
              selectedSlugs={selectedFilterSlugs}
              inheritedSlugs={categoryFilterIds}
              onToggleSlug={toggleFilterSlug}
              hasProductCatalog={hasProductCatalog}
              productFilterGroups={productFilterGroups}
              productFiltersLoading={productFiltersLoading}
              selectedProductFilterKeys={selectedProductFilterKeys}
              onToggleProductFilterKey={toggleProductFilterKey}
            />
          </SectionCard>

          {archived !== undefined && (
            <SectionCard title="Visibilidade">
              <Text style={styles.visibilityStatus}>
                {`Seu módulo está ${isArchived ? "arquivado" : "publicado"}.`}
              </Text>
              <Text style={styles.visibilityHint}>
                {isArchived
                  ? 'Clique em "Publicar Módulo" para ficar visível para seus alunos.'
                  : 'Clique em "Arquivar Módulo" para ficar invisível para seus alunos.'}
              </Text>
              <Pressable
                onPress={() =>
                  setUpdatedCard((prev) => ({ ...prev, archived: !prev.archived }))
                }
                style={({ hovered }) => [
                  styles.toggleBtn,
                  {
                    borderColor: isArchived ? ADMIN_COLORS.successBorder : ADMIN_COLORS.warnBorder,
                    backgroundColor: isArchived ? ADMIN_COLORS.successSoft : ADMIN_COLORS.warnBg,
                  },
                  hovered && { opacity: 0.85 },
                ]}
              >
                <Ionicons
                  name={isArchived ? "cloud-upload-outline" : "archive-outline"}
                  size={16}
                  color={isArchived ? ADMIN_COLORS.success : ADMIN_COLORS.warnText}
                />
                <Text
                  style={[
                    styles.toggleBtnText,
                    { color: isArchived ? ADMIN_COLORS.success : ADMIN_COLORS.warnText },
                  ]}
                >
                  {isArchived ? "Publicar Módulo" : "Arquivar Módulo"}
                </Text>
              </Pressable>
            </SectionCard>
          )}

          <View style={styles.actions}>
            <Pressable
              onPress={onClose}
              disabled={isSaving}
              style={({ hovered }) => [
                styles.btn,
                styles.btnSecondary,
                hovered && { backgroundColor: "#E5E7EB" },
              ]}
            >
              <Text style={[styles.btnText, { color: ADMIN_COLORS.secondaryText }]}>Cancelar</Text>
            </Pressable>
            <Pressable
              onPress={handleOnSaveClick}
              disabled={isSaving}
              style={({ hovered }) => [
                styles.btn,
                styles.btnPrimary,
                hovered && { backgroundColor: ADMIN_COLORS.accentDark },
                isSaving && { opacity: 0.7 },
              ]}
            >
              <Text style={[styles.btnText, { color: ADMIN_COLORS.primaryText }]}>
                {isSaving ? "Salvando..." : "Salvar"}
              </Text>
            </Pressable>
          </View>

          <AlertBox variant="admin"
            message={"Alteração feita com sucesso! :)"}
            visible={showSaveSuccessAlert}
            onClose={onClose}
            leftButton={"OK"}
            leftButtonFunction={() => setShowSaveSuccessAlert(false)}
          />
        </View>
      </ScrollView>
    </Provider>
  );
};

const styles = StyleSheet.create({
  switchRow: { flexDirection: "row", alignItems: "center", gap: 12 },
  switchHint: {
    fontFamily: ADMIN_FONT,
    fontSize: 12,
    color: ADMIN_COLORS.muted,
    marginTop: 2,
    lineHeight: 16,
    paddingHorizontal: 4,
  },
  warnBox: {
    marginTop: 12,
    backgroundColor: ADMIN_COLORS.warnBg,
    borderColor: ADMIN_COLORS.warnBorder,
    borderWidth: 1,
    borderRadius: 10,
    padding: 12,
  },
  warnText: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    color: ADMIN_COLORS.warnText,
    lineHeight: 18,
  },
  scrollContent: { alignItems: "center", padding: 20 },
  page: { width: "100%", maxWidth: 720 },
  pageTitle: {
    fontFamily: ADMIN_FONT,
    fontSize: 22,
    fontWeight: "700",
    color: ADMIN_COLORS.ink,
    marginBottom: 4,
  },
  pageSubtitle: {
    fontFamily: ADMIN_FONT,
    fontSize: 14,
    color: ADMIN_COLORS.muted,
    marginBottom: 20,
    lineHeight: 20,
  },
  visibilityStatus: {
    fontFamily: ADMIN_FONT,
    fontSize: 14,
    fontWeight: "600",
    color: ADMIN_COLORS.ink,
    marginBottom: 4,
  },
  visibilityHint: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    color: ADMIN_COLORS.muted,
    marginBottom: 14,
    lineHeight: 18,
  },
  toggleBtn: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    gap: 8,
    borderWidth: 1,
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 14,
    cursor: "pointer",
  },
  toggleBtnText: { fontFamily: ADMIN_FONT, fontSize: 14, fontWeight: "600" },
  actions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 12,
    marginTop: 4,
    marginBottom: 24,
  },
  btn: {
    minHeight: 44,
    borderRadius: 10,
    paddingHorizontal: 22,
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  btnPrimary: { backgroundColor: ADMIN_COLORS.accent },
  btnSecondary: { backgroundColor: ADMIN_COLORS.secondaryBg },
  btnText: { fontFamily: ADMIN_FONT, fontSize: 14, fontWeight: "600" },
});

export default EditCardInformationModal;
